import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import secureLocalStorage from "react-secure-storage"
import { createStudentHubConnection } from "services/signalr/studentHub"
import { server } from "services/config"
import { useAuthContext } from "./AuthContext"
import { NOTIFICATION_TYPES } from "common/constants"
import GamesCoin from "components/Coin/GamesCoin"
import useSound from "use-sound"
import { select } from "utils/sounds"
import { useNavigate, useLocation } from "react-router-dom"
import { routes } from "common/navigationRoutes"
import { ACTIVITY_VIEW_MODES } from "pages/student/activity/common/constants"
import useIndependentWorkApi from "modules/independentWork"
import useRealTimeInstructionApi from "modules/realTimeInstruction"
import SharedDeviceModal from "components/Coin/SharedDeviceModal"
import StartGameButton from "../components/Coin/StartGameButton"
import { useTranslation } from "react-i18next"
import useApi from "modules/activities"

const StudentHubContext = createContext(null)

export function StudentHubProvider({ children }) {
  const subscribersRef = useRef(new Set())
  const hubRef = useRef(null)
  const { userData, selectedGroupData, audioSettings } = useAuthContext()
  const [channel, setChannel] = useState("")
  const [prevSelectedGroupData, setPrevSelectedGroupData] = useState(null)
  const [showSmashGameNotification, setShowSmashGameNotification] =
    useState(false)
  const [showIndepenteWorkNotification, setShowIndepenteWorkNotification] =
    useState(false)
  const [isSharedDeviceMode, setIsSharedDeviceMode] = useState(false)
  const [headerCache, setHeaderCache] = useState(null)
  const [showSharedDevicesModal, setShowSharedDevicesModal] = useState(false)
  const headerCacheRef = useRef(null)
  const [teamData, setTeamData] = useState(null)
  const teamDataRef = useRef(null)
  const [notificationData, setNotificationData] = useState({})
  const notificationDataRef = useRef({})
  const [playSelect] = useSound(select, {
    volume: 0.5,
    soundEnabled: audioSettings?.enabledAudioEffects,
    html5: false,
  })
  const navigate = useNavigate()
  const location = useLocation()
  const { setTeamActive, addTeam } = useIndependentWorkApi()
  const { setTeamActive: setGameTeamActive, addTeam: addGameTeam } =
    useRealTimeInstructionApi()
  const { t } = useTranslation()
  const [allowShowNotification, setAllowShowNotification] = useState(true)
  const { getActivities } = useApi()
  const [moduleProgress, setModuleProgress] = useState(0)

  useEffect(() => {
    if (
      !userData?.userId ||
      userData?.userId <= 0 ||
      !selectedGroupData?.groupId
    ) {
      const { stop } = hubRef.current || {}
      if (stop) stop().catch(() => { })
      return
    }
    if (prevSelectedGroupData?.groupId !== selectedGroupData?.groupId) {
      if (prevSelectedGroupData?.groupId > 0) {
        const { stop } = hubRef.current || {}
        if (stop) stop().catch(() => { })
      }
      setPrevSelectedGroupData(selectedGroupData)
    }
    setChannel("")
    init()
  }, [userData?.userId, selectedGroupData?.groupId])

  useEffect(() => {
    teamDataRef.current = teamData
  }, [teamData])

  const accessTokenFactory = () => {
    const token = secureLocalStorage.getItem("smashAuthToken")
    return token || ""
  }

  const init = async () => {
    if (
      !userData ||
      userData?.userId < 0 ||
      !selectedGroupData ||
      selectedGroupData?.groupId < 0
    ) {
      setChannel("")
      return
    }

    const channelNme = `group-${selectedGroupData.groupId}-notifications`
    setChannel(channelNme)
    hubRef.current = createStudentHubConnection({
      baseUrl: server,
      channel: channelNme,
      accessTokenFactory,
    })

    const { start, oldConnection } = hubRef.current
    window.testSignalR = oldConnection
    start(
      { channel: channelNme, userId: userData?.userId },
      showNotification
    ).catch((e) => console.warn("SignalR start failed", e))

    return () => {
      const { stop } = hubRef.current || {}
      if (stop) stop().catch(() => { })
    }
  }

  const showNotification = useCallback(
    (payload) => {
      if (process.env.REACT_APP_ENVIORNMENT !== "production") {
        console.log("GamesHub notification:", payload)
      }
      if (payload.notificationType === NOTIFICATION_TYPES.SMASH_GAME) {
        const isStudentIn =
          payload?.students?.includes(userData?.userId) ||
          payload?.students?.length === 0
        if (!isStudentIn && payload?.students !== undefined) {
          return
        }
        const show = [1, 2].includes(payload.gameStatus)

        const { gameCode } = notificationDataRef.current
        if (!show || gameCode !== payload.gameCode) {
          setShowSmashGameNotification(false)
          setShowSharedDevicesModal(false)
          setHeaderCache(null)
          setTeamData(null)
          headerCacheRef.current = null
          if (!show) {
            return
          }
        }
        setNotificationData(payload)
        notificationDataRef.current = payload

        const header = headerCacheRef.current?.gameCode
        if (header) {
          return
        }
        setShowSmashGameNotification(true)
        setIsSharedDeviceMode(payload?.sharedDevices)
      }
      if (payload.notificationType === NOTIFICATION_TYPES.INDEPENDENT_WORK) {
        const isStudentIn =
          payload?.independentWork?.students?.includes(userData?.userId) ||
          payload?.independentWork?.students?.length === 0
        if (!isStudentIn && payload?.independentWork?.students !== undefined) {
          return
        }
        const { module } = notificationDataRef.current
        const show = payload.status === "started"
        if (
          !show ||
          module?.missionModuleId !== payload?.module?.missionModuleId
        ) {
          setShowIndepenteWorkNotification(false)
          setShowSharedDevicesModal(false)
          setHeaderCache(null)
          setTeamData(null)
          headerCacheRef.current = null
          if (!show) {
            return
          }
        }
        setNotificationData(payload)
        notificationDataRef.current = payload
        const header = headerCacheRef.current?.module
        if (header) {
          return
        }
        if (
          payload?.independentWork?.missionModuleId ===
          payload?.module?.missionModuleId
        ) {
          getActivities({
            group: selectedGroupData?.groupId,
            missionId: payload?.module?.missionId,
            missionModuleId: payload?.independentWork?.missionModuleId,
          }).then((response) => {
            setModuleProgress(response?.module?.progress || 0)
            setShowIndepenteWorkNotification(true)
          })
        } else {
          setShowIndepenteWorkNotification(true)
        }
        setIsSharedDeviceMode(payload?.independentWork?.sharedDevices)
      }
      if (
        payload.notificationType === NOTIFICATION_TYPES.INDEPENDENT_WORK_TEAM ||
        payload.notificationType ===
        NOTIFICATION_TYPES.REAL_TIME_INSTRUCTION_TEAM
      ) {
        if (teamDataRef.current !== null) {
          const teamMembers = teamDataRef.current?.teamMembers || []
          const memberIndex = teamMembers.findIndex(
            (member) => member.userId === payload?.userId
          )
          if (memberIndex >= 0) {
            teamMembers[memberIndex] = {
              ...teamMembers[memberIndex],
              ...payload,
            }
          } else {
            teamMembers.push(payload)
          }
          setTeamData({ ...teamDataRef.current, teamMembers })
        }
      }
    },
    [userData?.userId]
  )

  const subscribe = useCallback((cb) => {
    subscribersRef.current.add(cb)
    return () => subscribersRef.current.delete(cb)
  }, [])

  const acknowledge = useCallback(async () => {
    return true
  }, [])

  const startGame = () => {
    const { notificationType, gameCode, module, independentWork } =
      notificationDataRef.current
    playSelect()
    if (notificationType === NOTIFICATION_TYPES.SMASH_GAME) {
      if (isSharedDeviceMode) {
        const groupId = selectedGroupData?.groupId
        const gameId = notificationData?.gameId || 0
        addGameTeam({ groupId, gameId }).then((response) => {
          if (response.success) {
            setTeamData({
              notificationType: NOTIFICATION_TYPES.SMASH_GAME,
              ...response?.data,
            })
          }
        })
        setShowSharedDevicesModal(true)
        return
      }
      const currentDomain = window.location.origin
      window.location.href = `${currentDomain}/professor/smash/V2/Game?code=${gameCode}`
    }
    if (notificationType === NOTIFICATION_TYPES.INDEPENDENT_WORK) {
      if (isSharedDeviceMode) {
        const independentWorkId = independentWork?.independentWorkId || 0
        addTeam({ independentWorkId }).then((response) => {
          if (response.success) {
            setTeamData({
              notificationType: NOTIFICATION_TYPES.INDEPENDENT_WORK,
              ...response?.data,
            })
          }
        })
        setShowSharedDevicesModal(true)
        return
      }
      setTeamData(null)
      const missionModuleId = module?.missionModuleId
      const groupId = selectedGroupData?.groupId
      const missionId = module?.missionId
      const activityUrl = `${routes.ACTIVITY}?groupId=${groupId}&missionId=${missionId}&missionModuleId=${missionModuleId}&view=${ACTIVITY_VIEW_MODES.STUDENT}`
      navigate(activityUrl, { replace: true })
      window.location.reload()
    }
  }

  const createTeamAndStartIndependentWork = useCallback(
    (team) => {
      if (team) {
        setShowIndepenteWorkNotification(false)
        const { module, independentWork } = notificationData
        const teamMembers = independentWork?.teamMembers
        const currentTeam = teamMembers?.find(
          (member) =>
            member.userId === userData?.userId &&
            member.independentWorkId === independentWork?.independentWorkId
        )
        setTeamActive({
          independentWorkId: independentWork.independentWorkId,
          teamId: currentTeam?.teamId,
        })
          .then((response) => {
            const missionModuleId = module?.missionModuleId
            const groupId = selectedGroupData?.groupId
            const missionId = module?.missionId
            const activityUrl = `${routes.ACTIVITY}?groupId=${groupId}&missionId=${missionId}&missionModuleId=${missionModuleId}&view=${ACTIVITY_VIEW_MODES.STUDENT}`
            navigate(activityUrl, { replace: true })
            window.location.reload()
          })
          .catch((error) => {
            console.log("🚀 ~ setTeamActive ~ error:", error)
            setShowIndepenteWorkNotification(true)
          })
      } else {
        setShowIndepenteWorkNotification(true)
      }
    },
    [notificationData, selectedGroupData, isSharedDeviceMode]
  )

  const createTeamAndStartRealTimeInstructions = useCallback(
    (team) => {
      if (team) {
        setShowSmashGameNotification(false)
        const { gameCode, gameId } = notificationData
        const currentTeam = teamDataRef.current?.teamMember
        setGameTeamActive({
          teamId: currentTeam?.teamId,
          gameId,
          groupId: currentTeam.groupId,
        })
          .then((response) => {
            const currentDomain = window.location.origin
            window.location.href = `${currentDomain}/professor/smash/V2/Game?code=${gameCode}`
          })
          .catch((error) => {
            console.log("🚀 ~ setGameTeamActive ~ error:", error)
            setShowSmashGameNotification(true)
          })
      } else {
        setShowSmashGameNotification(true)
      }
    },
    [teamData]
  )

  const addHeaderButton = useCallback(() => {
    setShowSmashGameNotification(false)
    setShowIndepenteWorkNotification(false)
    setHeaderCache(notificationDataRef.current)
    headerCacheRef.current = notificationDataRef.current
  }, [headerCache, notificationData])

  const setSessionActivityLog = useCallback((params) => {
    const method = hubRef.current?.setSessionActivityLog
    if (method) {
      method(params)
    }
  }, [])

  const value = useMemo(
    () => ({
      channel,
      showSmashGameNotification,
      showIndepenteWorkNotification,
      isSharedDeviceMode,
      headerCache,
      showSharedDevicesModal,
      teamData,
      notificationData,
      isStudentHubActive:
        showIndepenteWorkNotification ||
        showSmashGameNotification ||
        showSharedDevicesModal,
      subscribe,
      acknowledge,
      showNotification,
      setAllowShowNotification,
      setSessionActivityLog,
    }),
    [
      channel,
      subscribe,
      acknowledge,
      notificationData,
      showIndepenteWorkNotification,
      showSmashGameNotification,
      showSharedDevicesModal,
      setSessionActivityLog,
    ]
  )
  return (
    <StudentHubContext.Provider value={value}>
      {children}
      {(!(location?.pathname || "").startsWith(routes.ACTIVITY) ||
        ((location?.pathname || "").startsWith(routes.ACTIVITY) &&
          moduleProgress !== 100)) &&
        allowShowNotification && (
          <GamesCoin
            open={showSmashGameNotification || showIndepenteWorkNotification}
            notificationType={notificationData.notificationType}
            onStart={startGame}
            onClose={addHeaderButton}
          />
        )}
      <SharedDeviceModal
        open={showSharedDevicesModal}
        onClose={(team) => {
          setShowSharedDevicesModal(false)
          if (teamData?.notificationType === NOTIFICATION_TYPES.SMASH_GAME) {
            createTeamAndStartRealTimeInstructions(team)
          }
          if (
            teamData?.notificationType === NOTIFICATION_TYPES.INDEPENDENT_WORK
          ) {
            createTeamAndStartIndependentWork(team)
          }
        }}
        teamData={teamData}
      />
      {!showSmashGameNotification &&
        !showIndepenteWorkNotification &&
        allowShowNotification &&
        ((headerCache?.gameCode !== undefined &&
          headerCache?.gameCode === notificationData?.gameCode) ||
          (headerCache?.module !== undefined &&
            headerCache?.module?.missionModuleId ===
            notificationData?.module?.missionModuleId)) && (
          <StartGameButton
            text={
              notificationData?.notificationType ===
                NOTIFICATION_TYPES.SMASH_GAME
                ? t("Let's play!")
                : t("Independent work")
            }
            onStart={startGame}
          />
        )}
    </StudentHubContext.Provider>
  )
}

export function useStudentHub() {
  const ctx = useContext(StudentHubContext)
  if (!ctx)
    throw new Error(
      "useStudentHub must be used within StudentHubProvider"
    )
  return ctx
}
